import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiFetch } from './apiBase';
import ReconNotes from './ReconNotes';
import ExceptionExportButtons from './ExceptionExportButtons';
import ExceptionTable from './ExceptionTable';
import { filterExceptionRows, filtersToSearchParams } from './reconExceptionFilters';
import { useExceptionFilters } from './useExceptionFilters';

export default function ElementExceptions({ hasReconResults = false, refreshKey = 0, runId = 'current' }) {
  const {
    filters,
    applied,
    filterOptions,
    updateFilter,
    applyFilters,
    clearAllFilters,
    refineRows,
    hasPendingApply,
    hasActiveFilters,
  } = useExceptionFilters({ enabled: hasReconResults, refreshKey });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [meta, setMeta] = useState(null);
  const limit = 100;

  useEffect(() => {
    setOffset(0);
  }, [applied]);

  const load = useCallback(async () => {
    if (!hasReconResults) {
      setLoading(false);
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = filtersToSearchParams(applied, { limit, offset });
      if (runId && runId !== 'current') params.set('runId', runId);
      let data;
      try {
        data = await apiFetch(`/api/recon/element-exceptions?${params}`);
      } catch (e) {
        if (e.status !== 404) throw e;
        const latest = await apiFetch('/api/recon/runs/latest');
        const all = (latest.exceptions || []).filter((ex) => ex.oracleElementName);
        if (!all.length) {
          throw new Error(
            'Could not load exceptions. Refresh the page or try again.'
          );
        }
        const filtered = filterExceptionRows(all, applied);
        data = {
          reconMode: 'element-wage-type',
          exceptions: filtered.slice(offset, offset + limit),
          total: filtered.length,
        };
      }
      const refined = refineRows(data.exceptions || []);
      setRows(refined);
      setTotal(data.total ?? 0);
      setMeta(data);
    } catch (e) {
      setError(e.message);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [hasReconResults, offset, applied, refreshKey, refineRows, runId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!hasReconResults) {
    return (
      <div className="card-panel flex flex-col items-center px-6 py-16 text-center">
        <p className="text-lg font-semibold text-slate-100">No recon results yet</p>
        <p className="mt-2 max-w-md text-sm text-slate-400">
          Run reconciliation on the <strong>Overview</strong> tab first. This view provides full exception
          filtering and export-style browsing.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="page-hero">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="section-title">Exception analysis</p>
            <h3 className="mt-1 text-xl font-bold text-slate-100">Element × wage type exceptions</h3>
            <p className="mt-2 text-sm text-slate-400">
              Filter by column; use <strong>Apply filters</strong> for amounts and search. Metric, issue,
              and element filters apply immediately where noted.
            </p>
          </div>
          <ExceptionExportButtons
            filters={applied}
            total={total}
            disabled={loading && rows.length === 0}
            className="shrink-0 lg:pt-6"
          />
        </div>
      </header>

      <ReconNotes linesNormalized={meta?.registerLinesNormalized} />

      {error && (
        <div className="alert-error">
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex gap-2 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} exceptions
            {hasActiveFilters ? ' (filtered)' : ''}
            {loading ? ' · updating…' : ''}
          </p>
          <ExceptionTable
            rows={rows}
            filterOptions={filterOptions}
            filters={filters}
            onFilterChange={updateFilter}
            onApply={applyFilters}
            onClearFilters={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            hasPendingApply={hasPendingApply}
            loading={loading}
          />
          {total > limit && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                Page {Math.floor(offset / limit) + 1} · {offset + 1}–{Math.min(offset + limit, total)} of{' '}
                {total.toLocaleString()}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset((o) => Math.max(0, o - limit))}
                  className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={offset + limit >= total || loading}
                  onClick={() => setOffset((o) => o + limit)}
                  className="btn-secondary px-2 py-1 text-xs disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
